import { useEffect, useState } from 'react';

import authFetch from '../../../utils/authFetch';
import './aiPromptLibrary.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const getResponseError = async (response, fallbackMessage) => {
  try {
    const data = await response.json();
    return data.error || data.detail || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

const AiPromptLibrary = ({ isAvailable }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [editedPrompt, setEditedPrompt] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!isAvailable || !isOpen) {
      return undefined;
    }

    const closeLibraryByEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', closeLibraryByEscape);
    return () => {
      document.removeEventListener('keydown', closeLibraryByEscape);
    };
  }, [isAvailable, isOpen]);

  if (!isAvailable) {
    return null;
  }

  const loadPrompts = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    setLoadError('');

    try {
      const response = await authFetch(`${API_URL}/ai-prompts/`);
      if (!response.ok) {
        throw new Error(
          await getResponseError(
            response,
            'Не удалось загрузить библиотеку промптов'
          )
        );
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Backend вернул некорректную библиотеку промптов');
      }

      setPrompts(data);
    } catch (error) {
      console.error('Ошибка загрузки библиотеки промптов:', error);
      setLoadError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLibrary = () => {
    const shouldOpen = !isOpen;
    setIsOpen(shouldOpen);

    if (shouldOpen) {
      loadPrompts();
    }
  };

  const openEditor = (prompt) => {
    setIsOpen(false);
    setSaveError('');
    setEditedPrompt({ ...prompt });
  };

  const closeEditor = () => {
    if (isSaving) {
      return;
    }

    setSaveError('');
    setEditedPrompt(null);
  };

  const updateEditedPrompt = (field, value) => {
    setEditedPrompt(currentPrompt => ({
      ...currentPrompt,
      [field]: value,
    }));
  };

  const savePrompt = async () => {
    if (!editedPrompt || isSaving) {
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      const response = await authFetch(
        `${API_URL}/ai-prompts/${encodeURIComponent(editedPrompt.logic_name)}/`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            system_prompt: editedPrompt.system_prompt,
            user_prompt: editedPrompt.user_prompt,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          await getResponseError(response, 'Не удалось сохранить промпт')
        );
      }

      const savedPrompt = await response.json();
      setPrompts(currentPrompts =>
        currentPrompts.map(prompt =>
          prompt.logic_name === savedPrompt.logic_name
            ? savedPrompt
            : prompt
        )
      );
      setEditedPrompt(null);
    } catch (error) {
      console.error('Ошибка сохранения промпта:', error);
      setSaveError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="ai-prompt-library">
      <button
        type="button"
        className="main-action-btn ai-prompt-library-button"
        aria-expanded={isOpen}
        aria-controls="ai-prompt-library-list"
        onClick={toggleLibrary}
      >
        Библиотека промптов
      </button>

      {isOpen && (
        <div
          id="ai-prompt-library-list"
          className="ai-prompt-library-list"
        >
          {isLoading && (
            <div className="ai-prompt-library-status">
              Загружаем промпты…
            </div>
          )}

          {!isLoading && loadError && (
            <div className="ai-prompt-library-status ai-prompt-library-error">
              <span>{loadError}</span>
              <button type="button" onClick={loadPrompts}>
                Повторить
              </button>
            </div>
          )}

          {!isLoading && !loadError && prompts.length === 0 && (
            <div className="ai-prompt-library-status">
              Промпты не найдены
            </div>
          )}

          {!isLoading && !loadError && prompts.map(prompt => (
            <button
              key={prompt.logic_name}
              type="button"
              className="ai-prompt-library-item"
              onClick={() => openEditor(prompt)}
            >
              {prompt.ui_name}
            </button>
          ))}
        </div>
      )}

      {editedPrompt && (
        <div className="ai-prompt-editor-overlay">
          <div
            className="ai-prompt-editor"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-prompt-editor-title"
          >
            <div className="ai-prompt-editor-header">
              <div>
                <h2 id="ai-prompt-editor-title">{editedPrompt.ui_name}</h2>
                <p>
                  Переменные обозначаются как{' '}
                  <code>{'{{variable_name}}'}</code>. Их изменение или
                  удаление изменит данные, передаваемые прикладной логикой.
                </p>
              </div>
              <button
                type="button"
                className="ai-prompt-editor-close"
                aria-label="Закрыть редактор промпта"
                disabled={isSaving}
                onClick={closeEditor}
              >
                ×
              </button>
            </div>

            <label htmlFor="ai-system-prompt">Системный промпт</label>
            <textarea
              id="ai-system-prompt"
              value={editedPrompt.system_prompt}
              disabled={isSaving}
              onChange={event =>
                updateEditedPrompt('system_prompt', event.target.value)
              }
            />

            <label htmlFor="ai-user-prompt">Пользовательский промпт</label>
            <textarea
              id="ai-user-prompt"
              value={editedPrompt.user_prompt}
              disabled={isSaving}
              onChange={event =>
                updateEditedPrompt('user_prompt', event.target.value)
              }
            />

            {saveError && (
              <div className="ai-prompt-editor-error" role="alert">
                {saveError}
              </div>
            )}

            <div className="ai-prompt-editor-actions">
              <button
                type="button"
                className="main-action-btn danger"
                disabled={isSaving}
                onClick={closeEditor}
              >
                Отмена
              </button>
              <button
                type="button"
                className="main-action-btn word"
                disabled={isSaving}
                onClick={savePrompt}
              >
                {isSaving ? 'Сохраняем…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiPromptLibrary;
